import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "@/hooks/useTranslation";
import { getReadableErrorMessage } from "@/utils/errorHandler";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Download, TrendingUp, Loader2, Filter, CheckSquare, Square, Instagram, User, Settings, FileText, HelpCircle, LogOut, Sparkles, ChevronDown, ChevronUp, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Footer from "@/components/Footer";
import LandingNavbar from "@/components/LandingNavbar";
import NavTabs from "@/components/NavTabs";
import { Link } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// OPARA v7.0 - Sacred Domain Detection
import { BACKEND_URL } from "@/utils/apiConfig";
import { translateEval, evalSortRank, evalKeyOf } from "@/utils/evalLabels";
import { formatNumberRuleD, formatFollowerCount } from "@/utils/formatNumber";

// v30.71h: Markalar hucresi — kapali: tek "Markalari gor (N)" butonu; acik: tum chip'ler + Gizle
// (veri/"(xN)"/backend/Excel DEGISMEZ, sadece render). t i18n PROP olarak gelir.
function BrandsCell({ brands, collabReels, t }) {
  const [open, setOpen] = useState(false);
  if (!brands || brands.length === 0) return <span className="text-gray-500">-</span>;
  const toggle = (next) => (e) => {
    if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
    if (e.type === 'keydown') e.preventDefault();
    setOpen(next);
  };
  // v30.71n: brand -> reel shortcode haritasi (chip'i ilgili reel'e linkler). "(xN)" strip + lowercase ile esle.
  const normBrand = (s) => (s || '').replace(/\s*\(x\d+\)\s*$/i, '').trim().toLowerCase();
  const map = {};
  (collabReels || []).forEach(c => (c.brands || []).forEach(b => {
    const k = normBrand(b);
    if (k && !map[k] && c.shortcode) map[k] = c.shortcode;
  }));
  if (!open) {
    return (
      <span
        role="button"
        tabIndex={0}
        onClick={toggle(true)}
        onKeyDown={toggle(true)}
        className="inline-block text-xs text-blue-300 bg-blue-900/30 hover:bg-blue-900/50 px-2 py-0.5 rounded cursor-pointer select-none whitespace-nowrap"
      >
        {t('reporting.show_brands')} ({brands.length})
      </span>
    );
  }
  return (
    <span>
      {brands.map((b, i) => {
        const sc = map[normBrand(b)];
        return sc ? (
          <a key={i} href={`https://www.instagram.com/reel/${sc}/`} target="_blank" rel="noopener noreferrer" className="inline-block text-xs text-blue-300 bg-blue-900/30 px-2 py-0.5 rounded mr-1 mb-1 whitespace-nowrap hover:underline cursor-pointer">
            {b}
          </a>
        ) : (
          <span key={i} className="inline-block text-xs text-blue-300 bg-blue-900/30 px-2 py-0.5 rounded mr-1 mb-1 whitespace-nowrap">
            {b}
          </span>
        );
      })}
      <span
        role="button"
        tabIndex={0}
        onClick={toggle(false)}
        onKeyDown={toggle(false)}
        className="inline-block text-xs text-gray-300 bg-gray-700 hover:bg-gray-600 px-2 py-0.5 rounded mr-1 mb-1 cursor-pointer select-none"
      >
        {t('reporting.hide_brands')}
      </span>
    </span>
  );
}

function RaporlamaPage() {
  const { user, token, logout, refreshUserData, setUser } = useAuth();
  const navigate = useNavigate();
  const { t, locale } = useTranslation();
  
  const [queryHistory, setQueryHistory] = useState([]);
  const [filteredQueries, setFilteredQueries] = useState([]);
  const [selectedQueries, setSelectedQueries] = useState([]);
  const [reportHistory, setReportHistory] = useState([]); // Past generated reports
  const [reportLimit, setReportLimit] = useState(5); // Report limit based on plan
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [sortBy, setSortBy] = useState("follower_count");
  const [sortOrder, setSortOrder] = useState("desc");
  const [reportQueries, setReportQueries] = useState([]); // Queries to remove from report
  const [showGenerateConfirmModal, setShowGenerateConfirmModal] = useState(false); // Generate report confirmation
  const [showExportConfirmModal, setShowExportConfirmModal] = useState(false); // Excel export confirmation (eski showConfirmModal)
  const [isDeductingCredits, setIsDeductingCredits] = useState(false); // Loading state for credit deduction
  const [dontAskAgain, setDontAskAgain] = useState(false); // "Beni tekrar uyarma" checkbox state
  const [reportName, setReportName] = useState(""); // Report name input
  const [currentReportId, setCurrentReportId] = useState(null); // Currently loaded report ID for saving
  const [isEditingReportName, setIsEditingReportName] = useState(false); // Report name input focus state
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false); // Advanced filters toggle
  
  // Filter states
  const [filters, setFilters] = useState({
    tarih_araligi: "tumu",
    takipci_sayisi: { type: "min", value: "" },
    reel_maliyeti: { type: "max", value: "" },
    beklenen_izlenme_ortalama: { type: "min", value: "" },
    izlenme_maliyeti: { type: "max", value: "" },
    ongorulen_reel_izlenmesi: { type: "min", value: "" },
    ortalama_toplam_etkilesim: { type: "min", value: "" },
    yaklasik_etkilesim_orani: { type: "min", value: "" },
    ongorulen_etkilesim_orani: { type: "min", value: "" },
    beklenen_story_ortalama: { type: "min", value: "" },
    beklenen_link_ortalama: { type: "min", value: "" },
    reel_aktiflik: { cok_yuksek: false, yuksek: false, ortalama: false, dusuk: false, cok_dusuk: false },
    gorunurluk_performansi: { dusuk: false, ortalama: false, yuksek: false },
    etkilesim_degerlendirme: { dusuk: false, ortalama: false, yuksek: false },
    is_birligi_sayisi: { type: "min", value: "" },
    marka_ara: ""
  });
  
  // Dropdown open/close states
  const [showDropdowns, setShowDropdowns] = useState({
    reel_aktiflik: false,
    gorunurluk_performansi: false,
    etkilesim_degerlendirme: false
  });
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setShowDropdowns({
          reel_aktiflik: false,
          gorunurluk_performansi: false,
          etkilesim_degerlendirme: false
        });
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Helper function: Calculate Follower Engagement Level based on follower count
  const calculateEngagementLevel = (followers, engagement) => {
    if (!followers || !engagement) return { rate: 0, level: 'Hesaplanamadı', color: 'text-gray-400' };
    
    const rate = (engagement / followers) * 100;
    
    // Determine segment and thresholds
    let lowThreshold, highThreshold;
    
    if (followers < 10000) {
      // NANO
      lowThreshold = 2.50;
      highThreshold = 4.50;
    } else if (followers < 50000) {
      // MIKRO_1
      lowThreshold = 2.25;
      highThreshold = 4.25;
    } else if (followers < 100000) {
      // MIKRO_2
      lowThreshold = 2.00;
      highThreshold = 4.00;
    } else if (followers < 200000) {
      // MAKRO_1
      lowThreshold = 1.75;
      highThreshold = 3.50;
    } else if (followers < 400000) {
      // MAKRO_2
      lowThreshold = 1.50;
      highThreshold = 3.00;
    } else if (followers < 600000) {
      // MAKRO_3
      lowThreshold = 1.25;
      highThreshold = 2.50;
    } else if (followers < 800000) {
      // MEGA_1
      lowThreshold = 1.00;
      highThreshold = 2.00;
    } else if (followers < 1000000) {
      // MEGA_2
      lowThreshold = 0.75;
      highThreshold = 1.75;
    } else if (followers < 2000000) {
      // CELEB_1
      lowThreshold = 0.50;
      highThreshold = 1.50;
    } else {
      // CELEB_2 (2M+)
      lowThreshold = 0.25;
      highThreshold = 1.25;
    }
    
    let level, color;
    if (rate < lowThreshold) {
      level = 'Düşük 🔴';
      color = 'text-red-400';
    } else if (rate <= highThreshold) {
      level = 'Ortalama 🟡';
      color = 'text-yellow-400';
    } else {
      level = 'Yüksek 🟢';
      color = 'text-green-400';
    }
    
    return { rate: rate.toFixed(2), level, color };
  };

  // v30.71d: Degerlendirme rozeti — emoji'den renk turet (AnalysisResults getColorClass paterni, dil-bagimsiz)
  const getEvalColors = (val) => {
    if (!val || val === '-') return null;
    // v30.71m: hesaplanamadi/yetersiz/na (emoji'siz kotu durumlar) -> Dusuk gibi KIRMIZI pill (dil-bagimsiz key)
    const key = evalKeyOf(val);
    if (key === 'hidden_likes' || key === 'insufficient_data' || key === 'na') {
      return { dot: '#EF4444', text: '#F87171', bg: 'rgba(239,68,68,0.15)' };
    }
    if (val.includes('🟢')) return { dot: '#22C55E', text: '#4ADE80', bg: 'rgba(34,197,94,0.15)' };
    if (val.includes('🟡')) return { dot: '#F5C518', text: '#FBBF24', bg: 'rgba(245,197,24,0.15)' };
    if (val.includes('🟠')) return { dot: '#FB923C', text: '#FB923C', bg: 'rgba(251,146,60,0.15)' };
    if (val.includes('🔴')) return { dot: '#EF4444', text: '#F87171', bg: 'rgba(239,68,68,0.15)' };
    return null;
  };

  // v30.71d: Metinden emoji + fazla bosluk temizle
  const cleanEvalLabel = (val) => (val || '').replace(/[🟢🟡🟠🔴]/g, '').trim();

  // v30.71d: Degerlendirme hucresi pill render'i (3 enum sutunu ortak)
  const renderEvalCell = (val, sortKey) => {
    const c = getEvalColors(val);  // renk ORIJINAL val'dan (emoji'li, dil-bagimsiz)
    // v30.71f: metni once UI diline cevir (translateEval), sonra emoji strip
    const label = cleanEvalLabel(translateEval(val, locale));
    return (
      <td className={`p-3 text-center ${sortBy === sortKey ? 'border-x border-[#FFDD59]/40' : ''}`}>
        {c ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '3px 10px', borderRadius: '999px', background: c.bg, color: c.text, fontWeight: 500, fontSize: '12.5px', whiteSpace: 'nowrap' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: c.dot }}></span>
            {label}
          </span>
        ) : (label || '-')}
      </td>
    );
  };

  // v30.71e: rapor tablo basligi — reporting.* key, trailing ':' strip (filtre etiketinden geliyor)
  const thLabel = (k) => (t(k) || '').replace(/:\s*$/, '');

  // Fetch query history
  useEffect(() => {
    // Allow unauthenticated users to view the page
    if (user && token) {
      fetchQueryHistory();
    } else {
      // Show empty state for non-logged users
      setLoading(false);
    }
  }, [user, token]);
  
  const fetchQueryHistory = async () => {
    try {
      // Fetch regular query history
      const response = await axios.get(`${BACKEND_URL}/api/user/query-history`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const queries = response.data.queries || [];
      setQueryHistory(queries);
      setFilteredQueries(queries);
      
      // Fetch report history
      const reportResponse = await axios.get(`${BACKEND_URL}/api/user/report-history`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const reports = reportResponse.data.reports || [];
      const limit = reportResponse.data.report_limit || 5;
      setReportHistory(reports);
      setReportLimit(limit);
      
    } catch (error) {
      console.error("Query/Report history fetch error:", error);
      // Silently handle error - don't show error toast
    } finally {
      setLoading(false);
    }
  };
  
  // Apply filters
  const applyFilters = () => {
    let filtered = [...queryHistory];
    
    // Tarih Aralığı
    if (filters.tarih_araligi && filters.tarih_araligi !== "tumu") {
      const now = new Date();
      const dayLimits = {
        "1gun": 1,
        "3gun": 3,
        "7gun": 7,
        "30gun": 30,
        "90gun": 90
      };
      const daysAgo = dayLimits[filters.tarih_araligi];
      const cutoffDate = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000));
      
      filtered = filtered.filter(q => {
        const queryDate = new Date(q.query_time || q.created_at);
        return queryDate >= cutoffDate;
      });
    }
    
    // Helper function to get opara and diger data
    const getOpara = (q) => q.result_data?.opara_core_metrikleri || {};
    const getDiger = (q) => q.result_data?.diger_metrikler || {};
    
    // Takipçi Sayısı
    if (filters.takipci_sayisi.value) {
      filtered = filtered.filter(q => {
        const value = q.follower_count || 0;
        const filterValue = parseFloat(filters.takipci_sayisi.value);
        return filters.takipci_sayisi.type === "min" ? value >= filterValue : value <= filterValue;
      });
    }
    
    // Reel İş Birliği Maliyeti TL
    if (filters.reel_maliyeti.value) {
      filtered = filtered.filter(q => {
        const opara = getOpara(q);
        const value = q.payment_per_reel || opara.reel_basina_maliyet_tl || 0;
        const filterValue = parseFloat(filters.reel_maliyeti.value);
        return filters.reel_maliyeti.type === "min" ? value >= filterValue : value <= filterValue;
      });
    }
    
    // Reel İş Birliği Beklenen Ortalama İzlenme
    if (filters.beklenen_izlenme_ortalama.value) {
      filtered = filtered.filter(q => {
        const opara = getOpara(q);
        const value = opara.beklenen_reel_izlenme_adet || 0;
        const filterValue = parseFloat(filters.beklenen_izlenme_ortalama.value);
        return filters.beklenen_izlenme_ortalama.type === "min" ? value >= filterValue : value <= filterValue;
      });
    }
    
    // İzlenme Başına Beklenen Yaklaşık Maliyet
    if (filters.izlenme_maliyeti.value) {
      filtered = filtered.filter(q => {
        const opara = getOpara(q);
        const value = opara.izlenme_basina_maliyet_tl || 0;
        const filterValue = parseFloat(filters.izlenme_maliyeti.value);
        return filters.izlenme_maliyeti.type === "min" ? value >= filterValue : value <= filterValue;
      });
    }
    
    // Öngörülen Reel İzlenmesi
    if (filters.ongorulen_reel_izlenmesi.value) {
      filtered = filtered.filter(q => {
        const opara = getOpara(q);
        const value = opara.beklenen_reel_izlenme_adet || 0;
        const filterValue = parseFloat(filters.ongorulen_reel_izlenmesi.value);
        return filters.ongorulen_reel_izlenmesi.type === "min" ? value >= filterValue : value <= filterValue;
      });
    }
    
    // Ortalama Toplam Etkileşim
    if (filters.ortalama_toplam_etkilesim.value) {
      filtered = filtered.filter(q => {
        const opara = getOpara(q);
        const value = opara.beklenen_is_birligi_etkilesimi_adet || 0;
        const filterValue = parseFloat(filters.ortalama_toplam_etkilesim.value);
        return filters.ortalama_toplam_etkilesim.type === "min" ? value >= filterValue : value <= filterValue;
      });
    }
    
    // Yaklaşık Etkileşim Oranı %
    if (filters.yaklasik_etkilesim_orani.value) {
      filtered = filtered.filter(q => {
        const opara = getOpara(q);
        const value = opara.beklenen_etkilesim_orani_yuzde || q.engagement_rate || 0;
        const filterValue = parseFloat(filters.yaklasik_etkilesim_orani.value);
        return filters.yaklasik_etkilesim_orani.type === "min" ? value >= filterValue : value <= filterValue;
      });
    }
    
    // Öngörülen Etkileşim Oranı (%)
    if (filters.ongorulen_etkilesim_orani.value) {
      filtered = filtered.filter(q => {
        const opara = getOpara(q);
        const value = opara.beklenen_etkilesim_orani_yuzde || 0;
        const filterValue = parseFloat(filters.ongorulen_etkilesim_orani.value);
        return filters.ongorulen_etkilesim_orani.type === "min" ? value >= filterValue : value <= filterValue;
      });
    }
    
    // Beklenen Story Ortalama İzlenme
    if (filters.beklenen_story_ortalama.value) {
      filtered = filtered.filter(q => {
        const opara = getOpara(q);
        const value = opara.beklenen_story_izlenme_mid || 0;
        const filterValue = parseFloat(filters.beklenen_story_ortalama.value);
        return filters.beklenen_story_ortalama.type === "min" ? value >= filterValue : value <= filterValue;
      });
    }
    
    // Beklenen Ortalama Link Tıklama
    if (filters.beklenen_link_ortalama.value) {
      filtered = filtered.filter(q => {
        const opara = getOpara(q);
        const min = opara.beklenen_link_tiklama_min || 0;
        const max = opara.beklenen_link_tiklama_max || 0;
        const value = (min + max) / 2;
        const filterValue = parseFloat(filters.beklenen_link_ortalama.value);
        return filters.beklenen_link_ortalama.type === "min" ? value >= filterValue : value <= filterValue;
      });
    }
    
    // Reel Paylaşım Aktifliği
    if (filters.reel_aktiflik.cok_yuksek || filters.reel_aktiflik.yuksek || filters.reel_aktiflik.ortalama || filters.reel_aktiflik.dusuk || filters.reel_aktiflik.cok_dusuk) {
      filtered = filtered.filter(q => {
        const diger = getDiger(q);
        const evaluation = diger.aktiflik_durumu || '-';
        return (filters.reel_aktiflik.cok_yuksek && evaluation === 'Çok Yüksek') ||
               (filters.reel_aktiflik.yuksek && evaluation === 'Yüksek') ||
               (filters.reel_aktiflik.ortalama && evaluation === 'Ortalama') ||
               (filters.reel_aktiflik.dusuk && evaluation === 'Düşük') ||
               (filters.reel_aktiflik.cok_dusuk && evaluation === 'Çok Düşük');
      });
    }
    
    // Görünürlük Performansı Değerlendirmesi
    if (filters.gorunurluk_performansi.dusuk || filters.gorunurluk_performansi.ortalama || filters.gorunurluk_performansi.yuksek) {
      filtered = filtered.filter(q => {
        const diger = getDiger(q);
        const evaluation = diger.gorunurluk_performansi_degerlendirme || '-';
        return (filters.gorunurluk_performansi.dusuk && evaluation === 'Düşük') ||
               (filters.gorunurluk_performansi.ortalama && evaluation === 'Ortalama') ||
               (filters.gorunurluk_performansi.yuksek && evaluation === 'Yüksek');
      });
    }
    
    // Reel Başına Arındırılmış Etkileşim
    if (filters.arindirilmis_etkilesim.value) {
      filtered = filtered.filter(q => {
        const opara = getOpara(q);
        const value = opara.reel_basina_etkilesim_adet || 0;
        const filterValue = parseFloat(filters.arindirilmis_etkilesim.value);
        return filters.arindirilmis_etkilesim.type === "min" ? value >= filterValue : value <= filterValue;
      });
    }
    
    // Etkileşim Oranı %
    if (filters.etkilesim_orani.value) {
      filtered = filtered.filter(q => {
        const opara = getOpara(q);
        const value = parseFloat(opara.takipci_baglilik_orani_yuzde) || 0;
        const filterValue = parseFloat(filters.etkilesim_orani.value);
        return filters.etkilesim_orani.type === "min" ? value >= filterValue : value <= filterValue;
      });
    }
    
    // Etkileşim Oranı Değerlendirme
    if (filters.etkilesim_degerlendirme.dusuk || filters.etkilesim_degerlendirme.ortalama || filters.etkilesim_degerlendirme.yuksek) {
      filtered = filtered.filter(q => {
        const opara = getOpara(q);
        const evaluation = opara.baglilik_oranı_derecelendirme || '-';
        return (filters.etkilesim_degerlendirme.dusuk && evaluation === 'Düşük 🔴') ||
               (filters.etkilesim_degerlendirme.ortalama && evaluation === 'Ortalama 🟡') ||
               (filters.etkilesim_degerlendirme.yuksek && evaluation === 'Yüksek 🟢');
      });
    }
    
    // İlk 12 Reel'deki İş Birliği Sayısı
    if (filters.is_birligi_sayisi.value) {
      filtered = filtered.filter(q => {
        const diger = getDiger(q);
        const value = diger.ilk_12_reeldeki_is_birligi_sayisi || 0;
        const filterValue = parseFloat(filters.is_birligi_sayisi.value);
        return filters.is_birligi_sayisi.type === "min" ? value >= filterValue : value <= filterValue;
      });
    }
    
    // Marka Ara
    if (filters.marka_ara && filters.marka_ara.trim()) {
      const searchTerm = filters.marka_ara.trim().toLowerCase();
      filtered = filtered.filter(q => {
        const diger = getDiger(q);
        const brands = diger.tespit_edilen_markalar || [];
        return brands.some(brand => brand.toLowerCase().includes(searchTerm));
      });
    }
    
    setFilteredQueries(filtered);
  };
  
  // Reset filters
  const resetFilters = () => {
    setFilters({
      tarih_araligi: "tumu",
      takipci_sayisi: { type: "min", value: "" },
      reel_maliyeti: { type: "max", value: "" },
      beklenen_izlenme_ortalama: { type: "min", value: "" },
      izlenme_maliyeti: { type: "max", value: "" },
      ongorulen_reel_izlenmesi: { type: "min", value: "" },
      ortalama_toplam_etkilesim: { type: "min", value: "" },
      yaklasik_etkilesim_orani: { type: "min", value: "" },
      ongorulen_etkilesim_orani: { type: "min", value: "" },
      beklenen_story_ortalama: { type: "min", value: "" },
      beklenen_link_ortalama: { type: "min", value: "" },
      reel_aktiflik: { cok_yuksek: false, yuksek: false, ortalama: false, dusuk: false, cok_dusuk: false },
      gorunurluk_performansi: { dusuk: false, ortalama: false, yuksek: false },
      etkilesim_degerlendirme: { dusuk: false, ortalama: false, yuksek: false },
      is_birligi_sayisi: { type: "min", value: "" },
      marka_ara: ""
    });
    setFilteredQueries(queryHistory);
  };
  
  // Toggle selection
  const toggleSelection = (queryId) => {
    if (selectedQueries.includes(queryId)) {
      setSelectedQueries(selectedQueries.filter(id => id !== queryId));
    } else {
      if (selectedQueries.length >= 500) {
        toast.error(t('reporting.max_selection'));
        return;
      }
      setSelectedQueries([...selectedQueries, queryId]);
    }
  };
  
  // Select all filtered
  const selectAll = () => {
    const allIds = filteredQueries.map(q => q.id);
    setSelectedQueries(allIds);
  };
  
  // Deselect all
  const deselectAll = () => {
    setSelectedQueries([]);
  };
  
  // Get selected queries data
  const getSelectedData = () => {
    return queryHistory
      .filter(q => selectedQueries.includes(q.id))
      .map(q => {
        // Access opara_core_metrikleri and diger_metrikler from result_data
        const opara = q.result_data?.opara_core_metrikleri || {};
        // v30.71b: worker 'other_metrics' (EN) yaziyor, 'diger_metrikler' DEGIL; other_metrics oncelikli, eski TR key geriye-uyum
        const diger = q.result_data?.other_metrics || q.result_data?.diger_metrikler || {};
        // v31 parite: Commit 1/useQueryHistory ifadesiyle AYNI insufficient sinyali (ekranla birebir gate)
        const validationM = q.result_data?.validation_status || q.result_data?.guvenlik_kontrolleri_durumu || {};
        const basket1CountM = validationM.basket_1_count ?? validationM.sepet_1_reel_sayisi ?? 0;
        const isInsufficientM = validationM.insufficient_data === true
          || q.result_data?.audit_status === 'INSUFFICIENT_DATA'
          || basket1CountM === 0;

        return {
          id: q.id,
          username: q.username,
          profile_picture: q.profile_picture || null,
          follower_count: q.follower_count || 0,
          payment_per_reel: q.payment_per_reel || opara.reel_basina_maliyet_tl || 0,
          // Beklenen İzlenme Aralığı (min-max ve ortalama)
          target_reel_views_min: opara.beklenen_izlenme_araligi_min || opara.expected_views_min || 0,
          target_reel_views_max: opara.beklenen_izlenme_araligi_max || opara.expected_views_max || 0,
          target_reel_views_avg: opara.beklenen_reel_izlenme_adet || opara.expected_views_avg || 0,
          target_reel_views_display: opara.beklenen_izlenme_araligi_display || opara.expected_views_display || '-',
          // İzlenme Başına Maliyet (string olarak geliyor, parse et)
          cost_per_view: (() => {
            const val = opara.izlenme_basina_maliyet_tl || opara.cpv;
            if (!val || val === 'N/A' || val === '-') return null;
            const parsed = parseFloat(val);
            return isNaN(parsed) ? null : parsed;
          })(),
          // Toplam Etkileşim (min-max ve ortalama)
          total_engagement_min: opara.beklenen_etkilesim_araligi_min || opara.expected_engagement_min || 0,
          total_engagement_max: opara.beklenen_etkilesim_araligi_max || opara.expected_engagement_max || 0,
          // v30.71b: expected_engagement_avg fmt_int STRING (virgul binlik "12,650") -> parse et (cost_per_view deseni)
          total_engagement_avg: (() => {
            const val = opara.beklenen_is_birligi_etkilesimi_adet || opara.expected_engagement_avg;
            if (!val || val === '-' || val === 'N/A') return null;
            const parsed = parseFloat(String(val).replace(/,/g, ''));
            return isNaN(parsed) ? null : parsed;
          })(),
          // Yaklaşık Etkileşim Oranı (string olarak geliyor "%0.86" gibi, parse et)
          engagement_rate: (() => {
            const val = opara.beklenen_etkilesim_orani_yuzde || opara.expected_er_percent;
            if (!val || val === '-' || val === 'N/A') return null;
            // Remove % sign and parse
            const cleaned = String(val).replace('%', '').trim();
            const parsed = parseFloat(cleaned);
            return isNaN(parsed) ? null : parsed;
          })(),
          // Beklenen Story İzlenme (aralık ve ortalama)
          story_view_min: opara.beklenen_story_izlenme_min || opara.story_views_min || 0,
          story_view_max: opara.beklenen_story_izlenme_max || opara.story_views_max || 0,
          story_view_avg: opara.beklenen_story_izlenme_mid || opara.story_views_avg || 0,
          // Beklenen Link Tıklama (aralık)
          link_click_min: opara.beklenen_link_tiklama_min || opara.link_clicks_min || 0,
          link_click_max: opara.beklenen_link_tiklama_max || opara.link_clicks_max || 0,
          // Reel Paylaşım Aktifliği
          velocity_evaluation: diger.aktiflik_durumu || diger.post_frequency_status || diger.activity_status || '-',
          reels_last_90_days: diger.son_90_gun_reel_sayisi || 0,
          is_insufficient_data: isInsufficientM,
          // Görünürlük Performansı
          visibility_evaluation: diger.gorunurluk_performansi_degerlendirme || diger.visibility_status || diger.visibility_rating || '-',
          // v30.71c: renk alani (Commit 3b rozet icin kullanacak, simdilik RENDER YOK)
          visibility_color: diger.gorunurluk_performansi_renk || diger.visibility_color || 'gray',
          visibility_avg_views: diger.gorunurluk_ortalama_izlenme || q.result_data?.other_metrics?.avg_reel_views || 0,
          // Reel Başına Arındırılmış Etkileşim
          follower_interaction: opara.reel_basina_etkilesim_adet || opara.engagement_per_reel || 0,
          // Etkileşim Oranı ve Değerlendirme (sayı olarak geliyor, 2 ondalık basamağa yuvarla)
          loyalty_rate: (() => {
            const val = opara.takipci_baglilik_orani_yuzde || opara.loyalty_rate_percent;
            if (!val && val !== 0) return null;
            const parsed = parseFloat(val);
            return isNaN(parsed) ? null : parseFloat(parsed.toFixed(2));
          })(),
          engagement_evaluation: opara.baglilik_oranı_derecelendirme || opara.loyalty_rating || '-',
          // İş Birliği Sayısı ve Markalar
          // v30.71b: uQH:184-198 paritesi — other_metrics.collaboration_count (diger artik other_metrics-first)
          paid_partnerships_count: (() => {
            const count = diger.ilk_12_reeldeki_is_birligi_sayisi || diger.collaborations_count || diger.collaboration_count;
            if (count != null) return count;
            const brands = diger.detected_brands || diger.tespit_edilen_markalar || [];
            if (Array.isArray(brands) && brands.length > 0) return brands.length;
            return 0;
          })(),
          // v30.71b: uQH:199-200 paritesi — other_metrics.detected_brands
          brand_mentions: diger.detected_brands || diger.tespit_edilen_markalar || [],
          result_data: q.result_data
        };
      });
  };
  
  // Sort data
  const getSortedData = () => {
    const data = getSelectedData();
    
    return data.sort((a, b) => {
      let aVal, bVal;
      
      switch(sortBy) {
        case "follower_count":
          aVal = a.follower_count;
          bVal = b.follower_count;
          break;
        case "payment_per_reel":
          aVal = a.payment_per_reel;
          bVal = b.payment_per_reel;
          break;
        case "target_reel_views_avg":
          aVal = (a.target_reel_views_min + a.target_reel_views_max) / 2;
          bVal = (b.target_reel_views_min + b.target_reel_views_max) / 2;
          break;
        case "cost_per_view":
          aVal = a.cost_per_view;
          bVal = b.cost_per_view;
          break;
        case "total_engagement_avg":
          aVal = a.total_engagement_avg;
          bVal = b.total_engagement_avg;
          break;
        case "engagement_rate":
          aVal = a.engagement_rate;
          bVal = b.engagement_rate;
          break;
        case "story_view_avg":
          aVal = a.story_view_avg;
          bVal = b.story_view_avg;
          break;
        case "link_click_avg":
          // Link tıklama için ortalama = (min + max) / 2
          aVal = (a.link_click_min + a.link_click_max) / 2;
          bVal = (b.link_click_min + b.link_click_max) / 2;
          break;
        case "follower_interaction":
          aVal = a.follower_interaction;
          bVal = b.follower_interaction;
          break;
        case "loyalty_rate":
          aVal = parseFloat(a.loyalty_rate) || 0;
          bVal = parseFloat(b.loyalty_rate) || 0;
          break;
        case "paid_partnerships_count":
          aVal = a.paid_partnerships_count;
          bVal = b.paid_partnerships_count;
          break;
        case "velocity_evaluation":
          aVal = evalSortRank(a.velocity_evaluation);
          bVal = evalSortRank(b.velocity_evaluation);
          break;
        case "visibility_evaluation":
          aVal = evalSortRank(a.visibility_evaluation);
          bVal = evalSortRank(b.visibility_evaluation);
          break;
        case "engagement_evaluation":
          aVal = evalSortRank(a.engagement_evaluation);
          bVal = evalSortRank(b.engagement_evaluation);
          break;
        case "avg_reel_views":
          aVal = a.avg_reel_views || a.visibility_avg_views || 0;
          bVal = b.avg_reel_views || b.visibility_avg_views || 0;
          break;
        default:
          return 0;
      }
      
      if (sortOrder === "desc") {
        return bVal - aVal;
      } else {
        return aVal - bVal;
      }
    });
  };
  
  // Handle sort with toggle
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order if same column
      setSortOrder(sortOrder === "desc" ? "asc" : "desc");
    } else {
      // New column - set default order
      setSortBy(column);
      // Sadece CPV için default artan, diğerleri azalan
      if (column === "cost_per_view") {
        setSortOrder("asc");
      } else {
        setSortOrder("desc");
      }
    }
  };
  
  // Generate report
  const handleGenerateReport = () => {
    // Check if user is logged in
    if (!user || !token) {
      toast.error(t('reporting.login_required'), {
        duration: 3000
      });
      return;
    }
    
    if (selectedQueries.length < 1) {
      toast.error(t('reporting.min_selection'));
      return;
    }
    
    // Check if user has "don't ask again" preference
    if (user?.skip_report_confirmation) {
      // Directly generate report without showing modal
      confirmGenerateReport();
    } else {
      // Show confirmation modal
      setShowGenerateConfirmModal(true);
    }
  };
  
  // Confirm report generation (no credit deduction)
  const confirmGenerateReport = async () => {
    setIsDeductingCredits(true);
    
    try {
      // If "don't ask again" is checked, save user preference
      if (dontAskAgain) {
        await axios.patch(
          `${BACKEND_URL}/api/user/preferences`,
          { skip_report_confirmation: true },
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        // Update local user object immediately
        if (user && setUser) {
          const updatedUser = {
            ...user,
            skip_report_confirmation: true
          };
          setUser(updatedUser);
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
      }
      
      // Create report with selected queries (no name yet)
      const reportResponse = await axios.post(
        `${BACKEND_URL}/api/reporting/create-report`,
        {
          query_ids: selectedQueries,
          report_name: null // Will be named later
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Get the newly created report ID
      const newReportId = reportResponse.data.report_id;
      
      // Close modal and show report
      setShowGenerateConfirmModal(false);
      setReportQueries([]);
      setShowReport(true);
      setDontAskAgain(false); // Reset checkbox
      setReportName(""); // Reset report name - user can enter new name
      setCurrentReportId(newReportId); // Set the new report ID for saving name later
      
      toast.success(t('reporting.success_report'));
      
      // Refresh report history
      fetchQueryHistory();
    } catch (error) {
      toast.error(getReadableErrorMessage(error, t, 'reporting.error_create'));
      setShowGenerateConfirmModal(false);
    } finally {
      setIsDeductingCredits(false);
    }
  };
  
  // Toggle report query for removal
  const toggleReportQuery = (queryId) => {
    if (reportQueries.includes(queryId)) {
      setReportQueries(reportQueries.filter(id => id !== queryId));
    } else {
      setReportQueries([...reportQueries, queryId]);
    }
  };
  
  // Remove selected from report
  const removeSelectedFromReport = () => {
    if (reportQueries.length === 0) {
      toast.error(t('reporting.min_remove'));
      return;
    }
    
    // Remove selected IDs from selectedQueries
    const newSelectedQueries = selectedQueries.filter(id => !reportQueries.includes(id));
    setSelectedQueries(newSelectedQueries);
    
    // Clear reportQueries
    setReportQueries([]);
    
    toast.success(t('reporting.removed_count', { count: reportQueries.length }));
  };
  
  // Export to Excel
  // Excel export - NO credit deduction (already deducted on "Raporla")
  const handleExportToExcel = async () => {
    // /rapor Excel artik backend endpoint uretir (build_report_excel_v2 = bulk ile ORTAK modul):
    // 24 kol v4 + logo + siyah ayrac + U-X highlight + influencer-link + likes-hidden etiket.
    const data = getSortedData();
    if (!data || data.length === 0) {
      toast.error(t('reporting.min_selection'));
      return;
    }
    // SIRALI query_ids (kullanicinin gordugu secili-sira) -> backend ayni sirada uretir
    const query_ids = data.map((item) => item.id).filter(Boolean);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/report/export-excel`,
        { query_ids, lang: locale },
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      // Blob -> indirme. Dosya adi eski format korunur (tarihli).
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Influencer_Raporu_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('reporting.success_excel'));
    } catch (err) {
      toast.error(getReadableErrorMessage(err));
    }
  };
  
  // Format number - for integers (binlikler virgülle, ondalık yok)
  const formatNumber = (num) => {
    if (!num && num !== 0) return "0";
    if (isNaN(num)) return "-";
    
    // Tam sayıya yuvarla
    const rounded = Math.round(num);
    
    // Binliklerle ayır (virgül ile)
    const formatted = rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    
    return formatted;
  };
  
  // Format currency - binlikler virgülle, ondalık yok (tam sayı)
  const formatCurrency = (num) => {
    if (!num && num !== 0) return "0";
    if (isNaN(num)) return "-";
    
    // Tam sayıya yuvarla
    const rounded = Math.round(num);
    
    // Binliklerle ayır (virgül ile)
    const formatted = rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    
    return formatted;
  };
  
  // Format number short
  const formatNumberShort = (num) => {
    if (!num) return "0";
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };
  
  const getPlanDisplayName = (planType) => {
    const planNames = {
      "free": t('reporting.plan_free'),
      "persona_staff": t('reporting.plan_staff'),
      "monthly_starter": t('reporting.plan_monthly_starter'),
      "monthly_growth": t('reporting.plan_monthly_growth'),
      "monthly_pro": t('reporting.plan_monthly_pro'),
      "onetime_starter": t('reporting.plan_onetime_starter'),
      "onetime_growth": t('reporting.plan_onetime_growth'),
      "onetime_pro": t('reporting.plan_onetime_pro')
    };
    return planNames[planType] || planType?.toUpperCase();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex flex-col">
      {/* Navbar - Show different navbars based on auth status */}
      {!user ? (
        <LandingNavbar />
      ) : (
        <NavTabs user={user} currentPage="rapor" />
      )}
      
      {/* Main Content - Full Width (No Sidebar) */}
      <div className="flex-1 px-4 md:px-8">
        
        {/* Title - Centered (yalniz secim ekraninda; rapor ekraninda dikey yer kazanmak icin gizli) */}
        {!showReport && (
          <div className="mb-8 pt-8">
            <h2 className="text-2xl font-bold text-[#FFDD59] text-center">
              {showReport ? t('reporting.page_title_report') : t('reporting.page_title')}
            </h2>
          </div>
        )}
        
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#FFDD59]" />
          </div>
        ) : (
          <>
            {/* Filter Section - Modern UX Design */}
            {!showReport && (
              <Card className="bg-gray-900 border border-gray-700 mb-6">
                <CardContent className="p-4">
                  {/* Primary Filter Row - Always Visible */}
                  <div className="flex items-end gap-3">
                    {/* Date Range */}
                    <div className="flex-1 max-w-[160px]">
                      <label className="block text-xs font-medium text-gray-400 mb-1.5">{t('reporting.date_filter')}</label>
                      <select 
                        value={filters.tarih_araligi} 
                        onChange={(e) => setFilters({...filters, tarih_araligi: e.target.value})} 
                        className="w-full bg-gray-800 border border-gray-600 text-white text-sm rounded-lg px-3 py-2 focus:border-[#FFDD59] focus:ring-1 focus:ring-[#FFDD59] transition-colors"
                      >
                        <option value="tumu">{t('reporting.date_all')}</option>
                        <option value="1gun">{t('reporting.date_1day')}</option>
                        <option value="3gun">{t('reporting.date_3days')}</option>
                        <option value="7gun">{t('reporting.date_7days')}</option>
                        <option value="30gun">{t('reporting.date_30days')}</option>
                        <option value="90gun">{t('reporting.date_90days')}</option>
                      </select>
                    </div>
                    
                    {/* Follower Count */}
                    <div className="flex-1 max-w-[200px]">
                      <label className="block text-xs font-medium text-gray-400 mb-1.5">{t('reporting.follower_count')}</label>
                      <div className="flex gap-1">
                        <select value={filters.takipci_sayisi.type} onChange={(e) => setFilters({...filters, takipci_sayisi: {...filters.takipci_sayisi, type: e.target.value}})} className="bg-gray-800 border border-gray-600 text-white text-sm rounded-lg px-2 py-2 w-16 focus:border-[#FFDD59] transition-colors">
                          <option value="min">Min</option>
                          <option value="max">Max</option>
                        </select>
                        <Input type="number" placeholder="0" value={filters.takipci_sayisi.value} onChange={(e) => setFilters({...filters, takipci_sayisi: {...filters.takipci_sayisi, value: e.target.value}})} className="flex-1 bg-gray-800 border-gray-600 text-white text-sm rounded-lg px-3 py-2 h-auto focus:border-[#FFDD59]" />
                      </div>
                    </div>
                    
                    {/* Posting Frequency */}
                    <div className="flex-1 max-w-[180px] relative">
                      <label className="block text-xs font-medium text-gray-400 mb-1.5">{t('reporting.frequency')}</label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => {
                            const newState = {...showDropdowns};
                            newState.reel_aktiflik = !newState.reel_aktiflik;
                            setShowDropdowns(newState);
                          }}
                          className="w-full bg-gray-800 border border-gray-600 text-white text-sm rounded-lg px-3 py-2 text-left flex justify-between items-center hover:border-gray-500 transition-colors"
                        >
                          <span className={!filters.reel_aktiflik.cok_yuksek && !filters.reel_aktiflik.yuksek && !filters.reel_aktiflik.ortalama && !filters.reel_aktiflik.dusuk && !filters.reel_aktiflik.cok_dusuk ? 'text-gray-400' : 'text-white'}>
                            {!filters.reel_aktiflik.cok_yuksek && !filters.reel_aktiflik.yuksek && !filters.reel_aktiflik.ortalama && !filters.reel_aktiflik.dusuk && !filters.reel_aktiflik.cok_dusuk
                              ? t('reporting.frequency_placeholder')
                              : [
                                  filters.reel_aktiflik.cok_yuksek && t('reporting.frequency_very_high'),
                                  filters.reel_aktiflik.yuksek && t('reporting.frequency_high'),
                                  filters.reel_aktiflik.ortalama && t('reporting.frequency_average'),
                                  filters.reel_aktiflik.dusuk && t('reporting.frequency_low'),
                                  filters.reel_aktiflik.cok_dusuk && t('reporting.frequency_very_low')
                                ].filter(Boolean).join(', ')
                            }
                          </span>
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        </button>
                        {showDropdowns.reel_aktiflik && (
                          <div className="absolute z-20 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-xl overflow-hidden">
                            <label className="flex items-center px-3 py-2.5 hover:bg-gray-700 cursor-pointer text-sm text-white transition-colors">
                              <input type="checkbox" checked={filters.reel_aktiflik.cok_yuksek} onChange={(e) => setFilters({...filters, reel_aktiflik: {...filters.reel_aktiflik, cok_yuksek: e.target.checked}})} className="mr-2 rounded" />
                              {t('reporting.frequency_very_high')}
                            </label>
                            <label className="flex items-center px-3 py-2.5 hover:bg-gray-700 cursor-pointer text-sm text-white transition-colors">
                              <input type="checkbox" checked={filters.reel_aktiflik.yuksek} onChange={(e) => setFilters({...filters, reel_aktiflik: {...filters.reel_aktiflik, yuksek: e.target.checked}})} className="mr-2 rounded" />
                              {t('reporting.frequency_high')}
                            </label>
                            <label className="flex items-center px-3 py-2.5 hover:bg-gray-700 cursor-pointer text-sm text-white transition-colors">
                              <input type="checkbox" checked={filters.reel_aktiflik.ortalama} onChange={(e) => setFilters({...filters, reel_aktiflik: {...filters.reel_aktiflik, ortalama: e.target.checked}})} className="mr-2 rounded" />
                              {t('reporting.frequency_average')}
                            </label>
                            <label className="flex items-center px-3 py-2.5 hover:bg-gray-700 cursor-pointer text-sm text-white transition-colors">
                              <input type="checkbox" checked={filters.reel_aktiflik.dusuk} onChange={(e) => setFilters({...filters, reel_aktiflik: {...filters.reel_aktiflik, dusuk: e.target.checked}})} className="mr-2 rounded" />
                              {t('reporting.frequency_low')}
                            </label>
                            <label className="flex items-center px-3 py-2.5 hover:bg-gray-700 cursor-pointer text-sm text-white transition-colors">
                              <input type="checkbox" checked={filters.reel_aktiflik.cok_dusuk} onChange={(e) => setFilters({...filters, reel_aktiflik: {...filters.reel_aktiflik, cok_dusuk: e.target.checked}})} className="mr-2 rounded" />
                              {t('reporting.frequency_very_low')}
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Spacer */}
                    <div className="flex-1" />
                    
                    {/* All Filters Button */}
                    <button
                      onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 border border-gray-600 rounded-lg hover:bg-gray-700 hover:border-gray-500 transition-all"
                    >
                      <SlidersHorizontal className="w-4 h-4" />
                      {showAdvancedFilters ? t('reporting.hide_filters') : t('reporting.show_filters')}
                      {showAdvancedFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    
                    {/* Apply Button */}
                    <Button
                      onClick={applyFilters}
                      className="bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-semibold px-5"
                    >
                      {t('reporting.apply_filters')}
                    </Button>
                  </div>
                  
                  {/* Advanced Filters - Slide Down Animation */}
                  <div className={`overflow-hidden transition-all duration-300 ease-in-out ${showAdvancedFilters ? 'max-h-[900px] opacity-100 mt-4' : 'max-h-0 opacity-0'}`}>
                    <div className="pt-4 border-t border-gray-700">
                      {/* Advanced Filters Grid - Yeni Sıralama */}
                      <div className="grid grid-cols-4 gap-4">
                        
                        {/* 1. Görünürlük Performansı Değerlendirme */}
                        <div className="relative">
                          <label className="block text-xs font-medium text-gray-400 mb-1.5">{t('reporting.visibility_perf')}</label>
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => {
                                const newState = {...showDropdowns};
                                newState.gorunurluk_performansi = !newState.gorunurluk_performansi;
                                setShowDropdowns(newState);
                              }}
                              className="w-full bg-gray-800 border border-gray-600 text-white text-sm rounded-lg px-3 py-2 text-left flex justify-between items-center hover:border-gray-500 transition-colors"
                            >
                              <span className={!filters.gorunurluk_performansi.dusuk && !filters.gorunurluk_performansi.ortalama && !filters.gorunurluk_performansi.yuksek ? 'text-gray-400' : 'text-white'}>
                                {!filters.gorunurluk_performansi.dusuk && !filters.gorunurluk_performansi.ortalama && !filters.gorunurluk_performansi.yuksek 
                                  ? t('reporting.frequency_placeholder')
                                  : [
                                      filters.gorunurluk_performansi.dusuk && t('reporting.eval_low'),
                                      filters.gorunurluk_performansi.ortalama && t('reporting.eval_avg'),
                                      filters.gorunurluk_performansi.yuksek && t('reporting.eval_high')
                                    ].filter(Boolean).join(', ')
                                }
                              </span>
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            </button>
                            {showDropdowns.gorunurluk_performansi && (
                              <div className="absolute z-20 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-xl overflow-hidden">
                                <label className="flex items-center px-3 py-2.5 hover:bg-gray-700 cursor-pointer text-sm text-white transition-colors">
                                  <input type="checkbox" checked={filters.gorunurluk_performansi.dusuk} onChange={(e) => setFilters({...filters, gorunurluk_performansi: {...filters.gorunurluk_performansi, dusuk: e.target.checked}})} className="mr-2 rounded" />
                                  {t('reporting.eval_low')}
                                </label>
                                <label className="flex items-center px-3 py-2.5 hover:bg-gray-700 cursor-pointer text-sm text-white transition-colors">
                                  <input type="checkbox" checked={filters.gorunurluk_performansi.ortalama} onChange={(e) => setFilters({...filters, gorunurluk_performansi: {...filters.gorunurluk_performansi, ortalama: e.target.checked}})} className="mr-2 rounded" />
                                  {t('reporting.eval_avg')}
                                </label>
                                <label className="flex items-center px-3 py-2.5 hover:bg-gray-700 cursor-pointer text-sm text-white transition-colors">
                                  <input type="checkbox" checked={filters.gorunurluk_performansi.yuksek} onChange={(e) => setFilters({...filters, gorunurluk_performansi: {...filters.gorunurluk_performansi, yuksek: e.target.checked}})} className="mr-2 rounded" />
                                  {t('reporting.eval_high')}
                                </label>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* 2. Ortalama Reel İzlenme */}
                        <div>
                          <label className="block text-xs font-medium text-gray-400 mb-1.5">{t('reporting.forecast_views')}</label>
                          <div className="flex gap-1">
                            <select value={filters.beklenen_izlenme_ortalama.type} onChange={(e) => setFilters({...filters, beklenen_izlenme_ortalama: {...filters.beklenen_izlenme_ortalama, type: e.target.value}})} className="bg-gray-800 border border-gray-600 text-white text-sm rounded-lg px-2 py-2 w-16">
                              <option value="min">Min</option>
                              <option value="max">Max</option>
                            </select>
                            <Input type="number" placeholder="0" value={filters.beklenen_izlenme_ortalama.value} onChange={(e) => setFilters({...filters, beklenen_izlenme_ortalama: {...filters.beklenen_izlenme_ortalama, value: e.target.value}})} className="flex-1 bg-gray-800 border-gray-600 text-white text-sm rounded-lg px-3 py-2 h-auto" />
                          </div>
                        </div>
                        
                        {/* 3. Etkileşim Oranı */}
                        <div>
                          <label className="block text-xs font-medium text-gray-400 mb-1.5">{t('reporting.approx_eng_rate')}</label>
                          <div className="flex gap-1">
                            <select value={filters.yaklasik_etkilesim_orani.type} onChange={(e) => setFilters({...filters, yaklasik_etkilesim_orani: {...filters.yaklasik_etkilesim_orani, type: e.target.value}})} className="bg-gray-800 border border-gray-600 text-white text-sm rounded-lg px-2 py-2 w-16">
                              <option value="min">Min</option>
                              <option value="max">Max</option>
                            </select>
                            <Input type="number" step="0.01" placeholder="0" value={filters.yaklasik_etkilesim_orani.value} onChange={(e) => setFilters({...filters, yaklasik_etkilesim_orani: {...filters.yaklasik_etkilesim_orani, value: e.target.value}})} className="flex-1 bg-gray-800 border-gray-600 text-white text-sm rounded-lg px-3 py-2 h-auto" />
                          </div>
                        </div>
                        
                        {/* 4. Etkileşim Değerlendirme */}
                        <div className="relative">
                          <label className="block text-xs font-medium text-gray-400 mb-1.5">{t('reporting.engagement_eval')}</label>
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => {
                                const newState = {...showDropdowns};
                                newState.etkilesim_degerlendirme = !newState.etkilesim_degerlendirme;
                                setShowDropdowns(newState);
                              }}
                              className="w-full bg-gray-800 border border-gray-600 text-white text-sm rounded-lg px-3 py-2 text-left flex justify-between items-center hover:border-gray-500 transition-colors"
                            >
                              <span className={!filters.etkilesim_degerlendirme.dusuk && !filters.etkilesim_degerlendirme.ortalama && !filters.etkilesim_degerlendirme.yuksek ? 'text-gray-400' : 'text-white'}>
                                {!filters.etkilesim_degerlendirme.dusuk && !filters.etkilesim_degerlendirme.ortalama && !filters.etkilesim_degerlendirme.yuksek 
                                  ? t('reporting.frequency_placeholder')
                                  : [
                                      filters.etkilesim_degerlendirme.dusuk && t('reporting.eval_low'),
                                      filters.etkilesim_degerlendirme.ortalama && t('reporting.eval_avg'),
                                      filters.etkilesim_degerlendirme.yuksek && t('reporting.eval_high')
                                    ].filter(Boolean).join(', ')
                                }
                              </span>
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            </button>
                            {showDropdowns.etkilesim_degerlendirme && (
                              <div className="absolute z-20 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-xl overflow-hidden">
                                <label className="flex items-center px-3 py-2.5 hover:bg-gray-700 cursor-pointer text-sm text-white transition-colors">
                                  <input type="checkbox" checked={filters.etkilesim_degerlendirme.dusuk} onChange={(e) => setFilters({...filters, etkilesim_degerlendirme: {...filters.etkilesim_degerlendirme, dusuk: e.target.checked}})} className="mr-2 rounded" />
                                  {t('reporting.eval_low')}
                                </label>
                                <label className="flex items-center px-3 py-2.5 hover:bg-gray-700 cursor-pointer text-sm text-white transition-colors">
                                  <input type="checkbox" checked={filters.etkilesim_degerlendirme.ortalama} onChange={(e) => setFilters({...filters, etkilesim_degerlendirme: {...filters.etkilesim_degerlendirme, ortalama: e.target.checked}})} className="mr-2 rounded" />
                                  {t('reporting.eval_avg')}
                                </label>
                                <label className="flex items-center px-3 py-2.5 hover:bg-gray-700 cursor-pointer text-sm text-white transition-colors">
                                  <input type="checkbox" checked={filters.etkilesim_degerlendirme.yuksek} onChange={(e) => setFilters({...filters, etkilesim_degerlendirme: {...filters.etkilesim_degerlendirme, yuksek: e.target.checked}})} className="mr-2 rounded" />
                                  {t('reporting.eval_high')}
                                </label>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* 5. İlk 12 Reel'deki İş Birliği Sayısı */}
                        <div>
                          <label className="block text-xs font-medium text-gray-400 mb-1.5">{t('reporting.collab_count')}</label>
                          <div className="flex gap-1">
                            <select value={filters.is_birligi_sayisi.type} onChange={(e) => setFilters({...filters, is_birligi_sayisi: {...filters.is_birligi_sayisi, type: e.target.value}})} className="bg-gray-800 border border-gray-600 text-white text-sm rounded-lg px-2 py-2 w-16">
                              <option value="min">Min</option>
                              <option value="max">Max</option>
                            </select>
                            <Input type="number" placeholder="0" value={filters.is_birligi_sayisi.value} onChange={(e) => setFilters({...filters, is_birligi_sayisi: {...filters.is_birligi_sayisi, value: e.target.value}})} className="flex-1 bg-gray-800 border-gray-600 text-white text-sm rounded-lg px-3 py-2 h-auto" />
                          </div>
                        </div>
                        
                        {/* 6. Reel İş Birliği Maliyeti */}
                        <div>
                          <label className="block text-xs font-medium text-gray-400 mb-1.5">{t('reporting.reel_cost')}</label>
                          <div className="flex gap-1">
                            <select value={filters.reel_maliyeti.type} onChange={(e) => setFilters({...filters, reel_maliyeti: {...filters.reel_maliyeti, type: e.target.value}})} className="bg-gray-800 border border-gray-600 text-white text-sm rounded-lg px-2 py-2 w-16">
                              <option value="min">Min</option>
                              <option value="max">Max</option>
                            </select>
                            <Input type="number" placeholder="0" value={filters.reel_maliyeti.value} onChange={(e) => setFilters({...filters, reel_maliyeti: {...filters.reel_maliyeti, value: e.target.value}})} className="flex-1 bg-gray-800 border-gray-600 text-white text-sm rounded-lg px-3 py-2 h-auto" />
                          </div>
                        </div>
                        
                        {/* 7. İzlenme Başına Beklenen Maliyet (CPV) */}
                        <div>
                          <label className="block text-xs font-medium text-gray-400 mb-1.5">{t('reporting.est_cpv')}</label>
                          <div className="flex gap-1">
                            <select value={filters.izlenme_maliyeti.type} onChange={(e) => setFilters({...filters, izlenme_maliyeti: {...filters.izlenme_maliyeti, type: e.target.value}})} className="bg-gray-800 border border-gray-600 text-white text-sm rounded-lg px-2 py-2 w-16">
                              <option value="min">Min</option>
                              <option value="max">Max</option>
                            </select>
                            <Input type="number" step="0.01" placeholder="0" value={filters.izlenme_maliyeti.value} onChange={(e) => setFilters({...filters, izlenme_maliyeti: {...filters.izlenme_maliyeti, value: e.target.value}})} className="flex-1 bg-gray-800 border-gray-600 text-white text-sm rounded-lg px-3 py-2 h-auto" />
                          </div>
                        </div>
                        
                        {/* 8. Öngörülen Reel İzlenmesi (YENİ) */}
                        <div>
                          <label className="block text-xs font-medium text-gray-400 mb-1.5">{t('reporting.forecast_reel_views')}</label>
                          <div className="flex gap-1">
                            <select value={filters.ongorulen_reel_izlenmesi.type} onChange={(e) => setFilters({...filters, ongorulen_reel_izlenmesi: {...filters.ongorulen_reel_izlenmesi, type: e.target.value}})} className="bg-gray-800 border border-gray-600 text-white text-sm rounded-lg px-2 py-2 w-16">
                              <option value="min">Min</option>
                              <option value="max">Max</option>
                            </select>
                            <Input type="number" placeholder="0" value={filters.ongorulen_reel_izlenmesi.value} onChange={(e) => setFilters({...filters, ongorulen_reel_izlenmesi: {...filters.ongorulen_reel_izlenmesi, value: e.target.value}})} className="flex-1 bg-gray-800 border-gray-600 text-white text-sm rounded-lg px-3 py-2 h-auto" />
                          </div>
                        </div>
                        
                        {/* 9. Öngörülen Etkileşim */}
                        <div>
                          <label className="block text-xs font-medium text-gray-400 mb-1.5">{t('reporting.avg_engagement')}</label>
                          <div className="flex gap-1">
                            <select value={filters.ortalama_toplam_etkilesim.type} onChange={(e) => setFilters({...filters, ortalama_toplam_etkilesim: {...filters.ortalama_toplam_etkilesim, type: e.target.value}})} className="bg-gray-800 border border-gray-600 text-white text-sm rounded-lg px-2 py-2 w-16">
                              <option value="min">Min</option>
                              <option value="max">Max</option>
                            </select>
                            <Input type="number" placeholder="0" value={filters.ortalama_toplam_etkilesim.value} onChange={(e) => setFilters({...filters, ortalama_toplam_etkilesim: {...filters.ortalama_toplam_etkilesim, value: e.target.value}})} className="flex-1 bg-gray-800 border-gray-600 text-white text-sm rounded-lg px-3 py-2 h-auto" />
                          </div>
                        </div>
                        
                        {/* 10. Öngörülen Etkileşim Oranı */}
                        <div>
                          <label className="block text-xs font-medium text-gray-400 mb-1.5">{t('reporting.forecast_eng_rate')}</label>
                          <div className="flex gap-1">
                            <select value={filters.ongorulen_etkilesim_orani.type} onChange={(e) => setFilters({...filters, ongorulen_etkilesim_orani: {...filters.ongorulen_etkilesim_orani, type: e.target.value}})} className="bg-gray-800 border border-gray-600 text-white text-sm rounded-lg px-2 py-2 w-16">
                              <option value="min">Min</option>
                              <option value="max">Max</option>
                            </select>
                            <Input type="number" step="0.01" placeholder="0%" value={filters.ongorulen_etkilesim_orani.value} onChange={(e) => setFilters({...filters, ongorulen_etkilesim_orani: {...filters.ongorulen_etkilesim_orani, value: e.target.value}})} className="flex-1 bg-gray-800 border-gray-600 text-white text-sm rounded-lg px-3 py-2 h-auto" />
                          </div>
                        </div>
                        
                        {/* 11. Öngörülen Story İzlenme (Takipçilerden) */}
                        <div>
                          <label className="block text-xs font-medium text-gray-400 mb-1.5">{t('reporting.forecast_story')}</label>
                          <div className="flex gap-1">
                            <select value={filters.beklenen_story_ortalama.type} onChange={(e) => setFilters({...filters, beklenen_story_ortalama: {...filters.beklenen_story_ortalama, type: e.target.value}})} className="bg-gray-800 border border-gray-600 text-white text-sm rounded-lg px-2 py-2 w-16">
                              <option value="min">Min</option>
                              <option value="max">Max</option>
                            </select>
                            <Input type="number" placeholder="0" value={filters.beklenen_story_ortalama.value} onChange={(e) => setFilters({...filters, beklenen_story_ortalama: {...filters.beklenen_story_ortalama, value: e.target.value}})} className="flex-1 bg-gray-800 border-gray-600 text-white text-sm rounded-lg px-3 py-2 h-auto" />
                          </div>
                        </div>
                        
                        {/* 12. Öngörülen Link Tıklaması */}
                        <div>
                          <label className="block text-xs font-medium text-gray-400 mb-1.5">{t('reporting.forecast_link')}</label>
                          <div className="flex gap-1">
                            <select value={filters.beklenen_link_ortalama.type} onChange={(e) => setFilters({...filters, beklenen_link_ortalama: {...filters.beklenen_link_ortalama, type: e.target.value}})} className="bg-gray-800 border border-gray-600 text-white text-sm rounded-lg px-2 py-2 w-16">
                              <option value="min">Min</option>
                              <option value="max">Max</option>
                            </select>
                            <Input type="number" placeholder="0" value={filters.beklenen_link_ortalama.value} onChange={(e) => setFilters({...filters, beklenen_link_ortalama: {...filters.beklenen_link_ortalama, value: e.target.value}})} className="flex-1 bg-gray-800 border-gray-600 text-white text-sm rounded-lg px-3 py-2 h-auto" />
                          </div>
                        </div>
                        
                      </div>
                      
                      {/* Reset Button in Advanced Section */}
                      <div className="flex justify-end mt-4">
                        <Button
                          onClick={resetFilters}
                          variant="ghost"
                          size="sm"
                          className="text-gray-400 hover:text-white hover:bg-gray-700"
                        >
                          {t('reporting.reset_filters')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Info + Selection Actions Row */}
            {!showReport && (
              <div className="flex items-center justify-between mb-6">
                {/* Left: Info text */}
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-400">
                    {selectedQueries.length > 0 ? (
                      <span className="text-[#FFDD59] font-medium">{t('reporting.selected_count', { count: selectedQueries.length })}</span>
                    ) : (
                      <span>{t('reporting.min_selection_info')}</span>
                    )}
                  </span>
                </div>
                
                {/* Right: Action Buttons */}
                <div className="flex items-center gap-2">
                  <Button
                    onClick={selectAll}
                    size="sm"
                    variant="outline"
                    className="border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white"
                  >
                    <CheckSquare className="w-4 h-4 mr-1.5" />
                    {t('reporting.select_all')} ({filteredQueries.length})
                  </Button>
                  <Button
                    onClick={deselectAll}
                    size="sm"
                    variant="ghost"
                    className="text-gray-400 hover:text-white hover:bg-gray-700"
                  >
                    <Square className="w-4 h-4 mr-1.5" />
                    {t('reporting.clear_all')}
                  </Button>
                  {/* v30.71: Ust 'Yeni Rapor Olustur' butonu (sm + turuncu CTA, alt buton ile ayni handler) */}
                  <Button
                    onClick={handleGenerateReport}
                    disabled={selectedQueries.length < 1}
                    size="sm"
                    style={{
                      backgroundColor: selectedQueries.length < 1 ? '#666666' : '#FF8C00',
                      color: '#FFFFFF',
                      fontWeight: 'bold'
                    }}
                    onMouseEnter={(e) => { if (selectedQueries.length >= 1) { e.currentTarget.style.backgroundColor = '#FF7700'; } }}
                    onMouseLeave={(e) => { if (selectedQueries.length >= 1) { e.currentTarget.style.backgroundColor = '#FF8C00'; } }}
                  >
                    <FileText className="w-4 h-4 mr-1.5" />
                    {t('reporting.gen_report_count', { count: selectedQueries.length })}
                  </Button>
                </div>
              </div>
            )}
            
            {/* Influencer Selection Grid */}
            {!showReport && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-8">
                  {filteredQueries.map((query) => (
                    <div
                      key={query.id}
                      onClick={() => toggleSelection(query.id)}
                      className={`group flex gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                        selectedQueries.includes(query.id)
                          ? 'bg-[#FFDD59]/10 border border-[#FFDD59]/50'
                          : 'bg-transparent border border-transparent hover:bg-white/5 hover:border-white/10'
                      }`}
                    >
                      {/* Avatar - Same as HomePage sidebar */}
                      <div className="shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full bg-slate-800 flex items-center justify-center overflow-hidden border border-white/10">
                        {query.profile_picture ? (
                          <img 
                            src={`https://wsrv.nl/?url=${encodeURIComponent(query.profile_picture)}&w=200&h=200&fit=cover`}
                            alt={query.username}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <span 
                          className="text-sm font-bold text-yellow-500"
                          style={query.profile_picture ? {display: 'none'} : {}}
                        >
                          {query.username.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0 flex flex-col justify-center">
                        {/* Row 1: Date & Checkbox */}
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-[10px] text-slate-500">{query.time_ago}</span>
                          <div className="p-0.5">
                            {selectedQueries.includes(query.id) ? (
                              <CheckSquare className="w-4 h-4 text-[#FFDD59]" />
                            ) : (
                              <Square className="w-4 h-4 text-slate-400 group-hover:text-slate-300" />
                            )}
                          </div>
                        </div>
                        
                        {/* Row 2: Username */}
                        <h4 className="text-sm font-semibold text-slate-200 truncate">
                          @{query.username}
                        </h4>

                        {/* Havuz: kim calistirdi - yalniz birden fazla farkli calistiran varsa */}
                        {filteredQueries.some(q => q.is_own === false) && (
                          <div className="text-[10px] text-slate-500 truncate">
                            {t('history.run_by')}: {query.run_by_name || t('history.unknown_user')}
                          </div>
                        )}

                        {/* Row 3: Follower Count */}
                        <div className="text-xs text-slate-400 mt-0.5">
                          {formatNumberShort(query.follower_count)} {t('reporting.followers_label')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Generate Report Button */}
                <div className="text-center">
                  <Button
                    onClick={handleGenerateReport}
                    disabled={selectedQueries.length < 1}
                    style={{
                      backgroundColor: selectedQueries.length < 1 ? '#666666' : '#FF8C00',
                      color: '#FFFFFF',
                      fontWeight: 'bold',
                      padding: '1.5rem 3rem',
                      fontSize: '1.25rem'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedQueries.length >= 1) {
                        e.currentTarget.style.backgroundColor = '#FF7700';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedQueries.length >= 1) {
                        e.currentTarget.style.backgroundColor = '#FF8C00';
                      }
                    }}
                  >
                    {t('reporting.gen_report_count', { count: selectedQueries.length })}
                  </Button>
                </div>
                
                {/* Bottom Spacing */}
                <div className="mb-12"></div>
              </>
            )}
            
            {/* Report Table */}
            {showReport && (
              <div className="w-full">
              <Card className="bg-gray-900 border-2 border-[#FFDD59]">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Report Name Input - After report is generated */}
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-gray-400 whitespace-nowrap">{t('reporting.report_name_label')}</label>
                        <input
                          type="text"
                          value={reportName}
                          onChange={(e) => setReportName(e.target.value)}
                          onFocus={() => setIsEditingReportName(true)}
                          onBlur={() => setIsEditingReportName(false)}
                          onKeyDown={async (e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              e.target.blur(); // Remove focus
                              
                              // Save report name
                              if (!reportName.trim()) {
                                toast.error(t('reporting.error_report_name'));
                                return;
                              }
                              
                              if (!currentReportId) {
                                toast.error(t('reporting.error_report_id'));
                                return;
                              }
                              
                              try {
                                await axios.patch(
                                  `${BACKEND_URL}/api/user/report/${currentReportId}/name`,
                                  { report_name: reportName.trim() },
                                  {
                                    headers: {
                                      'Authorization': `Bearer ${token}`
                                    }
                                  }
                                );
                                
                                // Refresh report history to show updated name
                                fetchQueryHistory();
                                
                                // Don't show toast for report name save
                              } catch (error) {
                                console.error("Failed to save report name:", error);
                                toast.error(t('reporting.error_save_name'));
                              }
                            }
                          }}
                          placeholder={t('reporting.report_name_placeholder')}
                          className={`px-3 py-1 border border-[#FFDD59] rounded text-sm w-44 transition-colors ${
                            isEditingReportName ? 'bg-[#FFDD59] text-black font-medium' : 'bg-gray-800 text-white'
                          }`}
                          maxLength={50}
                        />
                        <Button
                          onClick={async () => {
                            // Save report name
                            if (!reportName.trim()) {
                              toast.error(t('reporting.error_report_name'));
                              return;
                            }
                            
                            if (!currentReportId) {
                              toast.error(t('reporting.error_report_id'));
                              return;
                            }
                            
                            try {
                              await axios.patch(
                                `${BACKEND_URL}/api/user/report/${currentReportId}/name`,
                                { report_name: reportName.trim() },
                                {
                                  headers: {
                                    'Authorization': `Bearer ${token}`
                                  }
                                }
                              );
                              
                              // Refresh report history to show updated name
                              fetchQueryHistory();
                              
                              // Don't show toast for report name save
                            } catch (error) {
                              console.error("Failed to save report name:", error);
                              toast.error(t('reporting.error_save_name'));
                            }
                          }}
                          size="sm"
                          className="bg-[#FFDD59] hover:bg-[#E6C84D] text-black text-xs"
                        >
                          {t('reporting.save')}
                        </Button>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleExportToExcel}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {t('reporting.export_excel')}
                      </Button>
                      {reportQueries.length > 0 && (
                        <Button
                          onClick={removeSelectedFromReport}
                          size="sm"
                          className="bg-red-600 hover:bg-red-700 text-white"
                        >
                          {t('reporting.remove_selected')} ({reportQueries.length})
                        </Button>
                      )}
                      <Button
                        onClick={() => {
                          setShowReport(false);
                          setReportName("");
                          setCurrentReportId(null);
                        }}
                        size="sm"
                        style={{
                          backgroundColor: '#FF8C00',
                          color: '#FFFFFF',
                          fontWeight: '600'
                        }}
                        className="hover:opacity-90"
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FF7700'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FF8C00'}
                      >
                        {t('reporting.gen_report')}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  {/* Sort functionality moved to table headers - clickable columns */}
                  
                  {/* Table */}
                  <div className="overflow-auto max-h-[75vh]">
                    <table className="w-full text-sm">
                      <thead>
                        <tr>
                          <th colSpan={2} className="sticky top-0 z-20 bg-gray-800 h-[40px]"></th>
                          <th colSpan={5} className="text-center p-2 text-[#FFDD59] font-bold bg-gray-800 sticky top-0 z-20 h-[40px] border-2 border-[#FFDD59] rounded-t">{thLabel('reporting.group_influencer')}</th>
                          <th colSpan={6} className="text-center p-2 text-[#FFDD59] font-bold bg-gray-800 sticky top-0 z-20 h-[40px] border-2 border-[#FFDD59] rounded-t">{thLabel('reporting.group_current')}</th>
                          <th colSpan={6} className="text-center p-2 text-[#FFDD59] font-bold bg-gray-800 sticky top-0 z-20 h-[40px] border-2 border-[#FFDD59] rounded-t">{thLabel('reporting.group_forecast')}</th>
                        </tr>
                        <tr className="border-b-2 border-[#FFDD59]">
                          <th className="text-left p-3 text-[#FFDD59] font-bold w-[44px] bg-gray-800 sticky top-[40px] left-0 z-30">
                            <input
                              type="checkbox"
                              checked={reportQueries.length === getSortedData().length && getSortedData().length > 0}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setReportQueries(getSortedData().map(d => d.id));
                                } else {
                                  setReportQueries([]);
                                }
                              }}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </th>
                          <th className="text-left p-3 text-[#FFDD59] font-bold w-[52px] bg-gray-800 sticky top-[40px] left-[44px] z-30">#</th>
                          <th className="text-left p-3 text-[#FFDD59] font-bold border-2 border-gray-700 bg-gray-800 sticky top-[40px] left-[96px] z-30 border-l-2 border-l-[#FFDD59] border-b-2 border-b-[#FFDD59] w-[200px]">{thLabel('reporting.influencer')}</th>
                          {/* 4. Takipçi Sayısı */}
                          <th 
                            onClick={() => handleSort("follower_count")}
                            className="text-right p-3 text-[#FFDD59] font-bold cursor-pointer hover:bg-[#4c4d3e] transition-all border-2 border-gray-700 hover:border-[#FFDD59] bg-gray-800 sticky top-[40px] z-20 border-b-2 border-b-[#FFDD59]"
                          >
                            <div className="flex flex-col items-end gap-1">
                              <span>{thLabel('reporting.follower_count')}</span>
                              <span className="text-sm">{sortBy === "follower_count" ? (sortOrder === "desc" ? "↓" : "↑") : "⇅"}</span>
                            </div>
                          </th>
                          {/* 5. Reel İş Birliği Maliyeti TL */}
                          <th 
                            onClick={() => handleSort("payment_per_reel")}
                            className="text-right p-3 text-[#FFDD59] font-bold cursor-pointer hover:bg-[#4c4d3e] transition-all border-2 border-gray-700 hover:border-[#FFDD59] bg-gray-800 sticky top-[40px] z-20 border-b-2 border-b-[#FFDD59]"
                          >
                            <div className="flex flex-col items-end gap-1">
                              <span>{thLabel('reporting.reel_cost')}</span>
                              <span className="text-sm">{sortBy === "payment_per_reel" ? (sortOrder === "desc" ? "↓" : "↑") : "⇅"}</span>
                            </div>
                          </th>
                          {/* 17. İlk 12 Reel'deki İş Birliği Sayısı */}
                          <th 
                            onClick={() => handleSort("paid_partnerships_count")}
                            className="text-right p-3 text-[#FFDD59] font-bold cursor-pointer hover:bg-[#4c4d3e] transition-all border-2 border-gray-700 hover:border-[#FFDD59] bg-gray-800 sticky top-[40px] z-20 border-b-2 border-b-[#FFDD59]"
                          >
                            <div className="flex flex-col items-end gap-1">
                              <span>{thLabel('reporting.collab_count')}</span>
                              <span className="text-sm">{sortBy === "paid_partnerships_count" ? (sortOrder === "desc" ? "↓" : "↑") : "⇅"}</span>
                            </div>
                          </th>
                          {/* 18. Markalar */}
                          <th className="text-left p-3 text-[#FFDD59] font-bold border-2 border-gray-700 bg-gray-800 sticky top-[40px] z-20 border-r-2 border-r-[#FFDD59] border-b-2 border-b-[#FFDD59]">{thLabel('reporting.brands')}</th>
                          {/* 12. Reel Paylaşım Sıklığı */}
                          <th 
                            onClick={() => handleSort("velocity_evaluation")}
                            className="text-right p-3 text-[#FFDD59] font-bold cursor-pointer hover:bg-[#4c4d3e] transition-all border-2 border-gray-700 hover:border-[#FFDD59] bg-gray-800 sticky top-[40px] z-20 border-l-2 border-l-[#FFDD59] border-b-2 border-b-[#FFDD59]"
                          >
                            <div className="flex flex-col items-end gap-1">
                              <span>{thLabel('reporting.frequency')}</span>
                              <span className="text-sm">{sortBy === "velocity_evaluation" ? (sortOrder === "desc" ? "↓" : "↑") : "⇅"}</span>
                            </div>
                          </th>
                          {/* 13. Görünürlük Performansı Değerlendirme */}
                          <th 
                            onClick={() => handleSort("visibility_evaluation")}
                            className="text-right p-3 text-[#FFDD59] font-bold cursor-pointer hover:bg-[#4c4d3e] transition-all border-2 border-gray-700 hover:border-[#FFDD59] bg-gray-800 sticky top-[40px] z-20 border-b-2 border-b-[#FFDD59]"
                          >
                            <div className="flex flex-col items-end gap-1">
                              <span>{thLabel('reporting.visibility_perf')}</span>
                              <span className="text-sm">{sortBy === "visibility_evaluation" ? (sortOrder === "desc" ? "↓" : "↑") : "⇅"}</span>
                            </div>
                          </th>
                          {/* 16. Etkileşim Değerlendirme */}
                          <th 
                            onClick={() => handleSort("engagement_evaluation")}
                            className="text-right p-3 text-[#FFDD59] font-bold cursor-pointer hover:bg-[#4c4d3e] transition-all border-2 border-gray-700 hover:border-[#FFDD59] bg-gray-800 sticky top-[40px] z-20 border-b-2 border-b-[#FFDD59]"
                          >
                            <div className="flex flex-col items-end gap-1">
                              <span>{thLabel('reporting.engagement_eval')}</span>
                              <span className="text-sm">{sortBy === "engagement_evaluation" ? (sortOrder === "desc" ? "↓" : "↑") : "⇅"}</span>
                            </div>
                          </th>
                          {/* 14. Ortalama Reel İzlenme */}
                          <th 
                            onClick={() => handleSort("avg_reel_views")}
                            className="text-right p-3 text-[#FFDD59] font-bold cursor-pointer hover:bg-[#4c4d3e] transition-all border-2 border-gray-700 hover:border-[#FFDD59] bg-gray-800 sticky top-[40px] z-20 border-b-2 border-b-[#FFDD59]"
                          >
                            <div className="flex flex-col items-end gap-1">
                              <span>{thLabel('reporting.adjusted_views')}</span>
                              <span className="text-sm">{sortBy === "avg_reel_views" ? (sortOrder === "desc" ? "↓" : "↑") : "⇅"}</span>
                            </div>
                          </th>
                          {/* 14b. Reel Başına Arındırılmış Etkileşim (ADET) */}
                          <th
                            onClick={() => handleSort("follower_interaction")}
                            className="text-right p-3 text-[#FFDD59] font-bold cursor-pointer hover:bg-[#4c4d3e] transition-all border-2 border-gray-700 hover:border-[#FFDD59] bg-gray-800 sticky top-[40px] z-20 border-b-2 border-b-[#FFDD59]"
                          >
                            <div className="flex flex-col items-end gap-1">
                              <span>{thLabel('reporting.adjusted_engagement')}</span>
                              <span className="text-sm">{sortBy === "follower_interaction" ? (sortOrder === "desc" ? "↓" : "↑") : "⇅"}</span>
                            </div>
                          </th>
                          {/* 15. Reel Başına Arındırılmış Etkileşim Oranı */}
                          <th
                            onClick={() => handleSort("loyalty_rate")}
                            className="text-right p-3 text-[#FFDD59] font-bold cursor-pointer hover:bg-[#4c4d3e] transition-all border-2 border-gray-700 hover:border-[#FFDD59] bg-gray-800 sticky top-[40px] z-20 border-r-2 border-r-[#FFDD59] border-b-2 border-b-[#FFDD59]"
                          >
                            <div className="flex flex-col items-end gap-1">
                              <span>{thLabel('reporting.approx_eng_rate')}</span>
                              <span className="text-sm">{sortBy === "loyalty_rate" ? (sortOrder === "desc" ? "↓" : "↑") : "⇅"}</span>
                            </div>
                          </th>
                          {/* 6. Öngörülen İzlenme Başına Maliyet (CPV) */}
                          <th 
                            onClick={() => handleSort("cost_per_view")}
                            className="text-right p-3 text-[#FFDD59] font-bold cursor-pointer hover:bg-[#4c4d3e] transition-all border-2 border-gray-700 hover:border-[#FFDD59] bg-gray-800 sticky top-[40px] z-20 border-l-2 border-l-[#FFDD59] border-b-2 border-b-[#FFDD59]"
                          >
                            <div className="flex flex-col items-end gap-1">
                              <span>{thLabel('reporting.est_cpv')}</span>
                              <span className="text-sm">{sortBy === "cost_per_view" ? (sortOrder === "desc" ? "↓" : "↑") : "⇅"}</span>
                            </div>
                          </th>
                          {/* 7. Öngörülen Reel İzlenmesi */}
                          <th 
                            onClick={() => handleSort("target_reel_views_avg")}
                            className="text-right p-3 text-[#FFDD59] font-bold cursor-pointer hover:bg-[#4c4d3e] transition-all border-2 border-gray-700 hover:border-[#FFDD59] bg-gray-800 sticky top-[40px] z-20 border-b-2 border-b-[#FFDD59]"
                          >
                            <div className="flex flex-col items-end gap-1">
                              <span>{thLabel('reporting.forecast_reel_views')}</span>
                              <span className="text-sm">{sortBy === "target_reel_views_avg" ? (sortOrder === "desc" ? "↓" : "↑") : "⇅"}</span>
                            </div>
                          </th>
                          {/* 8. Öngörülen Etkileşim */}
                          <th 
                            onClick={() => handleSort("total_engagement_avg")}
                            className="text-right p-3 text-[#FFDD59] font-bold cursor-pointer hover:bg-[#4c4d3e] transition-all border-2 border-gray-700 hover:border-[#FFDD59] bg-gray-800 sticky top-[40px] z-20 border-b-2 border-b-[#FFDD59]"
                          >
                            <div className="flex flex-col items-end gap-1">
                              <span>{thLabel('reporting.avg_engagement')}</span>
                              <span className="text-sm">{sortBy === "total_engagement_avg" ? (sortOrder === "desc" ? "↓" : "↑") : "⇅"}</span>
                            </div>
                          </th>
                          {/* 9. Öngörülen Etkileşim Oranı */}
                          <th 
                            onClick={() => handleSort("engagement_rate")}
                            className="text-right p-3 text-[#FFDD59] font-bold cursor-pointer hover:bg-[#4c4d3e] transition-all border-2 border-gray-700 hover:border-[#FFDD59] bg-gray-800 sticky top-[40px] z-20 border-b-2 border-b-[#FFDD59]"
                          >
                            <div className="flex flex-col items-end gap-1">
                              <span>{thLabel('reporting.forecast_eng_rate')}</span>
                              <span className="text-sm">{sortBy === "engagement_rate" ? (sortOrder === "desc" ? "↓" : "↑") : "⇅"}</span>
                            </div>
                          </th>
                          {/* 10. Öngörülen Story İzlenme (Takipçilerden) */}
                          <th 
                            onClick={() => handleSort("story_view_avg")}
                            className="text-right p-3 text-[#FFDD59] font-bold cursor-pointer hover:bg-[#4c4d3e] transition-all border-2 border-gray-700 hover:border-[#FFDD59] bg-gray-800 sticky top-[40px] z-20 border-b-2 border-b-[#FFDD59]"
                          >
                            <div className="flex flex-col items-end gap-1">
                              <span>{thLabel('reporting.forecast_story')}</span>
                              <span className="text-sm">{sortBy === "story_view_avg" ? (sortOrder === "desc" ? "↓" : "↑") : "⇅"}</span>
                            </div>
                          </th>
                          {/* 11. Öngörülen Link Tıklaması */}
                          <th 
                            onClick={() => handleSort("link_click_avg")}
                            className="text-right p-3 text-[#FFDD59] font-bold cursor-pointer hover:bg-[#4c4d3e] transition-all border-2 border-gray-700 hover:border-[#FFDD59] bg-gray-800 sticky top-[40px] z-20 border-r-2 border-r-[#FFDD59] border-b-2 border-b-[#FFDD59]"
                          >
                            <div className="flex flex-col items-end gap-1">
                              <span>{thLabel('reporting.forecast_link')}</span>
                              <span className="text-sm">{sortBy === "link_click_avg" ? (sortOrder === "desc" ? "↓" : "↑") : "⇅"}</span>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {getSortedData().map((item, index) => (
                          <tr key={item.id} className="group border-b border-gray-700 hover:bg-gray-800/50">
                            <td className="p-3 w-[44px] sticky left-0 z-10 bg-gray-900 group-hover:bg-gray-800">
                              <input
                                type="checkbox"
                                checked={reportQueries.includes(item.id)}
                                onChange={() => toggleReportQuery(item.id)}
                                className="w-4 h-4 cursor-pointer"
                              />
                            </td>
                            <td className="p-3 text-gray-400 w-[52px] sticky left-[44px] z-10 bg-gray-900 group-hover:bg-gray-800">{index + 1}</td>
                            <td className="p-3 text-white font-semibold w-[200px] sticky left-[96px] z-10 bg-gray-900 group-hover:bg-gray-800">
                              {item.username ? (
                                <a
                                  href={`https://www.instagram.com/${item.username}/`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-400 underline hover:text-blue-300"
                                >
                                  @{item.username}
                                </a>
                              ) : (
                                <>@{item.username}</>
                              )}
                            </td>
                            {/* 4. Takipçi Sayısı */}
                            <td className={`p-3 text-right ${sortBy === "follower_count" ? "text-[#FFDD59] font-bold" : "text-white"}`}>
                              {formatFollowerCount(item.follower_count)}
                            </td>
                            {/* 5. Reel İş Birliği Maliyeti TL */}
                            <td className={`p-3 text-right ${sortBy === "payment_per_reel" ? "text-[#FFDD59] font-bold" : "text-white"}`}>
                              {formatCurrency(item.payment_per_reel)}
                            </td>
                            {/* 17. İlk 12 Reel'deki İş Birliği Sayısı */}
                            <td className={`p-3 text-right ${sortBy === "paid_partnerships_count" ? "text-[#FFDD59] font-bold" : "text-white"}`}>
                              {item.paid_partnerships_count || 0}
                            </td>
                            {/* 18. Markalar */}
                            <td className="p-3 text-left text-white text-sm">
                              <BrandsCell brands={item.brand_mentions} collabReels={item.result_data?.collaborations_by_reel || item.result_data?.other_metrics?.collaboration_reels || []} t={t} />
                            </td>
                            {/* 12. Reel Paylaşım Sıklığı */}
                            {renderEvalCell(item.velocity_evaluation, "velocity_evaluation")}
                            {/* 13. Görünürlük Performansı Değerlendirme */}
                            {renderEvalCell(item.visibility_evaluation, "visibility_evaluation")}
                            {/* 16. Etkileşim Değerlendirme */}
                            {renderEvalCell(item.engagement_evaluation, "engagement_evaluation")}
                            {/* 14. Ortalama Reel İzlenme */}
                            <td className={`p-3 text-right ${sortBy === "avg_reel_views" ? "text-[#FFDD59] font-bold" : "text-white"}`}>
                              {formatNumberRuleD(item.avg_reel_views || item.visibility_avg_views)}
                            </td>
                            {/* 14b. Reel Başına Arındırılmış Etkileşim (ADET) */}
                            <td className={`p-3 text-right ${sortBy === "follower_interaction" ? "text-[#FFDD59] font-bold" : "text-white"}`}>
                              {item.follower_interaction > 0 ? formatNumberRuleD(item.follower_interaction) : '-'}
                            </td>
                            {/* 15. Reel Başına Arındırılmış Etkileşim Oranı */}
                            <td className={`p-3 text-right ${sortBy === "loyalty_rate" ? "text-[#FFDD59] font-bold" : "text-white"}`}>
                              {item.loyalty_rate !== null ? `%${item.loyalty_rate.toFixed(2)}` : '-'}
                            </td>
                            {/* 6. Öngörülen İzlenme Başına Maliyet (CPV) */}
                            <td className={`p-3 text-right ${sortBy === "cost_per_view" ? "text-[#FFDD59] font-bold" : "text-white"}`}>
                              {item.cost_per_view && typeof item.cost_per_view === 'number' ? `${item.cost_per_view.toFixed(2)}` : '-'}
                            </td>
                            {/* 7. Öngörülen Reel İzlenmesi */}
                            <td className={`p-3 whitespace-nowrap text-right ${sortBy === "target_reel_views_avg" ? "text-[#FFDD59] font-bold" : "text-white"}`}>
                              {item.target_reel_views_display !== '-' ? item.target_reel_views_display : `${formatNumberRuleD(item.target_reel_views_min)} - ${formatNumberRuleD(item.target_reel_views_max)}`}
                            </td>
                            {/* 8. Öngörülen Etkileşim */}
                            <td className={`p-3 whitespace-nowrap text-right ${sortBy === "total_engagement_avg" ? "text-[#FFDD59] font-bold" : "text-white"}`}>
                              {item.total_engagement_min && item.total_engagement_max
                                ? `${formatNumberRuleD(item.total_engagement_min)} - ${formatNumberRuleD(item.total_engagement_max)}`
                                : formatNumberRuleD(item.total_engagement_avg)}
                            </td>
                            {/* 9. Öngörülen Etkileşim Oranı */}
                            <td className={`p-3 text-right ${sortBy === "engagement_rate" ? "text-[#FFDD59] font-bold" : "text-white"}`}>
                              {item.engagement_rate !== null ? `%${item.engagement_rate.toFixed(2)}` : '-'}
                            </td>
                            {/* 10. Öngörülen Story İzlenme (Takipçilerden) */}
                            <td className={`p-3 whitespace-nowrap text-right ${sortBy === "story_view_avg" ? "text-[#FFDD59] font-bold" : "text-white"}`}>
                              {item.story_view_min && item.story_view_max
                                ? `${formatNumberRuleD(item.story_view_min)} - ${formatNumberRuleD(item.story_view_max)}`
                                : '-'}
                            </td>
                            {/* 11. Öngörülen Link Tıklaması */}
                            <td className={`p-3 whitespace-nowrap text-right ${sortBy === "link_click_avg" ? "text-[#FFDD59] font-bold" : "text-white"}`}>
                              {item.link_click_min && item.link_click_max
                                ? `${formatNumber(item.link_click_min)} - ${formatNumber(item.link_click_max)}`
                                : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                </CardContent>
              </Card>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Generate Report Confirmation Modal - "Raporla" button */}
      {showGenerateConfirmModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <Card className="bg-gray-900 border-2 border-[#FFDD59] max-w-md w-full">
            <CardContent className="p-6">
              <h3 className="text-xl font-bold text-white mb-6 text-center">
                {t('reporting.modal_title')}
              </h3>

              <div className="space-y-4">
                <p className="text-gray-300 text-base leading-relaxed">
                  {t('reporting.modal_free_body').split('{free}')[0]}
                  <span className="text-green-400 font-bold">{t('reporting.modal_free_word')}</span>
                  {t('reporting.modal_free_body').split('{free}')[1]}
                </p>
              </div>

              <div className="flex gap-4 mt-6">
                <Button
                  onClick={() => {
                    setShowGenerateConfirmModal(false);
                    setDontAskAgain(false);
                  }}
                  variant="outline"
                  className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
                  disabled={isDeductingCredits}
                >
                  {t('reporting.modal_cancel')}
                </Button>
                <Button
                  onClick={confirmGenerateReport}
                  className="flex-1 bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold"
                  disabled={isDeductingCredits}
                >
                  {isDeductingCredits ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('common.loading')}
                    </>
                  ) : (
                    t('reporting.modal_confirm')
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      <Footer />
    </div>
  );
}

export default RaporlamaPage;
